import { useEffect, useState, useMemo } from 'react';
import PageHeader from '../../../components/ui/PageHeader';
import Skeleton from '../../../components/ui/Skeleton';
import StatusFolderRow from '../../../components/dailyAudit/StatusFolderRow';
import { useShop } from '../../../hooks/useShop';
import { api } from '../../../api/client';
import { CalendarDays } from 'lucide-react';

const PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 60 days', days: 60 },
];

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isoNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - (n - 1));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function rangeMatchesPreset(from, to, days) {
  return from === isoNDaysAgo(days) && to === isoToday();
}

export default function DailyAuditPage() {
  const { currentShop, currentShopId } = useShop();
  const noShop = !currentShopId;

  const [from, setFrom] = useState(isoNDaysAgo(30));
  const [to, setTo] = useState(isoToday());

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activePreset = useMemo(() => {
    return PRESETS.find(p => rangeMatchesPreset(from, to, p.days))?.days || null;
  }, [from, to]);

  useEffect(() => {
    if (!currentShopId || noShop) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.dailyAuditSummary(currentShopId, { from, to })
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message || String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [currentShopId, noShop, from, to]);

  const grouped = useMemo(() => {
    if (!data?.dates) return [];
    const out = [];
    let currentMonth = null;
    let bucket = null;
    for (const d of data.dates) {
      const dt = new Date(d.date + 'T00:00:00');
      const monthKey = dt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (monthKey !== currentMonth) {
        currentMonth = monthKey;
        bucket = { month: monthKey, dates: [] };
        out.push(bucket);
      }
      bucket.dates.push(d);
    }
    return out;
  }, [data]);

  const shopName = currentShop?.shop_nickname || currentShop?.shop_name || '';

  return (
    <div>
      <PageHeader
        title="Daily Audit"
        description={
          noShop
            ? 'Pick a single shop from the top bar to see the audit folder for that shop.'
            : `Daily audit folder for ${shopName}. Click a date to drill into Statement Vendors or COD Checks for that day.`
        }
        meta={!noShop && (
          <div className="flex items-center gap-3 flex-wrap text-[12px]">
            <div className="flex items-center gap-1.5">
              {PRESETS.map(p => (
                <button
                  key={p.days}
                  onClick={() => { setFrom(isoNDaysAgo(p.days)); setTo(isoToday()); }}
                  className={`px-3 py-1.5 rounded-md transition-colors ${
                    activePreset === p.days
                      ? 'bg-[var(--color-warm-800)] text-white font-semibold'
                      : 'bg-white border border-[var(--color-warm-200)] text-[var(--color-warm-700)] hover:border-[var(--color-warm-400)]'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1.5 text-[var(--color-warm-600)]">
              <span className="text-[11px] uppercase tracking-wider text-[var(--color-warm-500)] mr-1">or custom:</span>
              <input
                type="date"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
                className="px-2 py-1 rounded-md border border-[var(--color-warm-200)] bg-white text-[var(--color-warm-800)] focus:outline-none focus:border-[var(--color-warm-500)]"
              />
              <span>→</span>
              <input
                type="date"
                value={to}
                min={from}
                max={isoToday()}
                onChange={(e) => setTo(e.target.value)}
                className="px-2 py-1 rounded-md border border-[var(--color-warm-200)] bg-white text-[var(--color-warm-800)] focus:outline-none focus:border-[var(--color-warm-500)]"
              />
            </div>
          </div>
        )}
      />

      {noShop && (
        <div className="rounded-xl border border-[var(--color-warm-200)] bg-white p-8 text-center">
          <CalendarDays className="w-10 h-10 text-[var(--color-warm-400)] mx-auto mb-3" />
          <p className="text-[14px] text-[var(--color-warm-700)]">
            Daily Audit is per-shop. Use the shop selector at the top to choose one.
          </p>
        </div>
      )}

      {!noShop && loading && (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      )}

      {!noShop && !loading && error && (
        <div className="rounded-xl border border-[var(--color-danger-300)] bg-[var(--color-danger-50)] p-4 text-[var(--color-danger-700)] text-[14px]">
          Failed to load: {error}
        </div>
      )}

      {!noShop && !loading && !error && grouped.length === 0 && (
        <div className="rounded-xl border border-[var(--color-warm-200)] bg-white p-8 text-center">
          <p className="text-[14px] text-[var(--color-warm-700)]">No dates in this range.</p>
        </div>
      )}

      {!noShop && !loading && !error && grouped.map(group => (
        <div key={group.month} className="mb-6">
          <h2 className="text-[12px] uppercase tracking-wider text-[var(--color-warm-500)] font-bold mb-2 px-1">
            {group.month}
          </h2>
          <div className="space-y-1.5">
            {group.dates.map(d => (
              <StatusFolderRow
                key={d.date}
                dateEntry={d}
                basePath="/auditor/daily-audit"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
